import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { radiologyOrderService, radiologyReportService, radiologyStudyService, radiologyReportTemplateService, generateRadiologyReportPDFBlob } from '../../lib/services.radiology';
import { paymentVerificationService } from '../../lib/services.paymentVerification';
import { notificationService } from '../../lib/services.notification';
import { useStore } from '../../lib/store';
import { getUserRoleFromDB } from '../../lib/rbac';
import toast from 'react-hot-toast';

export default function RadiologyReportEditor() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [findings, setFindings] = useState('');
  const [impression, setImpression] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'partial' | 'paid'>('pending');
  const [canRelease, setCanRelease] = useState(false);
  const { user } = useStore();

  useEffect(() => {
    loadData();
    checkPermissions();
  }, [orderId]);

  const checkPermissions = async () => {
    if (!user?.id) return;
    try {
      const role = await getUserRoleFromDB(user.id);
      setCanRelease(role === 'admin' || role === 'receptionist');
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
  };

  const loadData = async () => {
    if (!orderId) return;
    
    try {
      const orderData = await radiologyOrderService.getById(orderId);
      setOrder(orderData);
      
      const templatesData = await radiologyReportTemplateService.getAll();
      setTemplates(templatesData);
      
      const existingReport = await radiologyReportService.getByOrder(orderId);
      if (existingReport) {
        setFindings(existingReport.findings || '');
        setImpression(existingReport.impression || '');
        setRecommendations(existingReport.recommendations || '');
      }

      if (orderId) {
        const paymentCheck = await paymentVerificationService.verifyRadiologyOrderPayment(orderId);
        setPaymentStatus(paymentCheck.paymentStatus);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load order data');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = (template: any) => {
    const sections = template.body?.sections || [];
    const findingsSection = sections.find((s: any) => s.title === 'Findings');
    const impressionSection = sections.find((s: any) => s.title === 'Impression');
    
    if (findingsSection) setFindings(findingsSection.content);
    if (impressionSection) setImpression(impressionSection.content);
  };

  const handleSave = async () => {
    if (!orderId) return;
    
    setSaving(true);
    try {
      const existingReport = await radiologyReportService.getByOrder(orderId);
      
      if (existingReport) {
        await radiologyReportService.update(existingReport.id, {
          findings,
          impression,
          recommendations,
        });
      } else {
        await radiologyReportService.create({
          order_id: orderId,
          report_no: `RAD-${Date.now()}`,
          findings,
          impression,
          recommendations,
        });
      }
      
      toast.success('Report saved successfully');
    } catch (error) {
      console.error('Failed to save report:', error);
      toast.error('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async (forceRelease: boolean = false) => {
    if (!orderId || !user?.id) return;
    
    if (paymentStatus !== 'paid' && !forceRelease) {
      toast.error('Payment is pending. Report cannot be finalized. Admin/Reception can force release.');
      return;
    }

    setSaving(true);
    try {
      const existingReport = await radiologyReportService.getByOrder(orderId);
      
      if (!existingReport) {
        throw new Error('Report must be saved before finalizing');
      }

      const pdfBlob = await generateRadiologyReportPDFBlob({
        reportNo: existingReport.report_no,
        patient: order?.patient,
        orderId,
        modality: existingReport.modality,
        findings,
        impression,
        recommendations,
        finalizedBy: 'Radiologist',
        finalizedAt: new Date(),
      });

      const fileName = await radiologyReportService.uploadPDF(orderId, pdfBlob);
      const signedUrl = await radiologyReportService.getSignedUrl(fileName);

      const updateData: any = {
        pdf_url: fileName,
        finalized_at: new Date().toISOString(),
      };

      if (paymentStatus !== 'paid') {
        updateData.payment_hold = true;
      }

      await radiologyReportService.update(existingReport.id, updateData);

      if (forceRelease && existingReport.id) {
        await paymentVerificationService.releaseRadiologyReport(existingReport.id, user.id);
      }

      await radiologyOrderService.updateStatus(orderId, 'finalized');
      
      if (order?.patient) {
        await notificationService.notifyResultReady(
          order.patient_id,
          order.patient.name,
          'radiology',
          orderId
        );
      }

      if (forceRelease) {
        toast.success('Report finalized and released (payment pending)');
      } else {
        toast.success('Report finalized successfully');
      }
      navigate('/radiology');
    } catch (error) {
      console.error('Failed to finalize report:', error);
      toast.error('Failed to finalize report');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Radiology Report Editor</h1>
        <p className="text-gray-600">Order: {order?.order_no}</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 space-y-6 max-w-4xl">
        {templates.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Load Template
            </label>
            <select
              onChange={(e) => {
                const template = templates.find(t => t.id === e.target.value);
                if (template) loadTemplate(template);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">Select a template...</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Findings
          </label>
          <textarea
            value={findings}
            onChange={(e) => setFindings(e.target.value)}
            rows={8}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Enter findings..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Impression
          </label>
          <textarea
            value={impression}
            onChange={(e) => setImpression(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Enter impression..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recommendations
          </label>
          <textarea
            value={recommendations}
            onChange={(e) => setRecommendations(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Enter recommendations..."
          />
        </div>

        {/* Payment Status Warning */}
        {paymentStatus !== 'paid' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Payment Pending</h3>
                <p className="mt-1 text-sm text-yellow-700">
                  This order has {paymentStatus === 'partial' ? 'partial' : 'no'} payment. Report will be held until payment is complete.
                  {canRelease && ' As an admin/reception user, you can force release the report.'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-300"
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          {paymentStatus !== 'paid' && canRelease ? (
            <button
              onClick={() => handleFinalize(true)}
              disabled={saving || !findings || !impression}
              className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-md hover:bg-orange-700 disabled:bg-gray-300"
            >
              {saving ? 'Processing...' : 'Force Release Report'}
            </button>
          ) : (
            <button
              onClick={() => handleFinalize(false)}
              disabled={saving || !findings || !impression || paymentStatus !== 'paid'}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-300"
            >
              {saving ? 'Processing...' : 'Finalize & Generate PDF'}
            </button>
          )}
          <button
            onClick={() => navigate('/radiology')}
            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
